import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, AlertTriangle, GraduationCap, Send } from "lucide-react";

/**
 * Il test di apprendimento, dal telefono del partecipante.
 *
 * Nessun login: in aula i partecipanti un account non ce l'hanno, hanno un
 * telefono. Si inquadra il QR proiettato (o si scrive il codice), si sceglie
 * il proprio nome fra quelli di quell'aula e si risponde. La correzione la fa
 * il server: qui le risposte giuste non arrivano nemmeno, altrimenti
 * basterebbe guardare la pagina.
 *
 * E' pensata per un dito su uno schermo piccolo: una domanda sotto l'altra,
 * risposte grandi da toccare, e il pulsante di consegna che si accende solo
 * quando non ne manca nessuna.
 */
export default function TestFormazione() {
  const { codice: dallUrl } = useParams();
  const [codice, setCodice] = useState(dallUrl || "");
  const [scritto, setScritto] = useState(dallUrl || "");
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(null);
  const [chiSono, setChiSono] = useState("");
  const [risposte, setRisposte] = useState({});
  const [esito, setEsito] = useState(null);
  const [invio, setInvio] = useState(false);

  const carica = useCallback(async (c) => {
    if (!c) return;
    setErrore(null);
    try {
      const { data } = await api.get(`/public/formazione/test/${c.toUpperCase()}`);
      setDati(data);
    } catch (e) {
      setDati(null);
      setErrore(e.response?.data?.detail || "Test non trovato");
    }
  }, []);

  useEffect(() => { if (dallUrl) carica(dallUrl); }, [dallUrl, carica]);

  const consegna = async () => {
    if (!chiSono) return;
    setInvio(true);
    try {
      const { data } = await api.post(
        `/public/formazione/test/${codice.toUpperCase()}`,
        { employee_id: chiSono, risposte });
      setEsito(data);
    } catch (e) {
      setErrore(e.response?.data?.detail || "Non è stato possibile consegnare");
    } finally { setInvio(false); }
  };

  const quante = dati?.domande?.length || 0;
  const date = Object.keys(risposte).length;
  const io = dati?.partecipanti?.find((p) => p.id === chiSono);

  /* ---------------------------------------------------- il risultato */
  if (esito) {
    const bene = esito.superato;
    return (
      <Schermo>
        <Card className={bene ? "border-emerald-300" : "border-amber-300"}>
          <CardContent className="p-6 text-center">
            {bene
              ? <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-3" />
              : <AlertTriangle className="w-14 h-14 text-amber-600 mx-auto mb-3" />}
            <h2 className="font-display text-3xl font-black tracking-tighter">
              {esito.punteggio} su {esito.su}
            </h2>
            <p className="text-lg font-semibold mt-1">{esito.percentuale}%</p>
            <p className={`mt-3 font-semibold ${bene ? "text-emerald-700" : "text-amber-700"}`}>
              {bene ? "Verifica superata" : `Non superata: serviva almeno il ${esito.soglia}%`}
            </p>
            <p className="text-sm text-slate-500 mt-3">
              {bene
                ? "Puoi consegnare il telefono e tornare al posto."
                : "Avvisa il docente: deciderà lui come procedere."}
            </p>
          </CardContent>
        </Card>
      </Schermo>
    );
  }

  /* ------------------------------------------------- il codice da scrivere */
  if (!dati) {
    return (
      <Schermo>
        <Card className="border-slate-200">
          <CardContent className="p-5 space-y-3">
            <h2 className="font-display text-xl font-black">Test di apprendimento</h2>
            <p className="text-sm text-slate-500">
              Scrivi il codice che vedi proiettato in aula.
            </p>
            <Input value={scritto} onChange={(e) => setScritto(e.target.value.toUpperCase())}
                   placeholder="ES. 4F2A9C" className="h-12 text-center text-lg tracking-[0.3em]"
                   data-testid="codice-test" />
            {errore && <p className="text-sm text-red-600">{errore}</p>}
            <Button className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white"
                    onClick={() => { setCodice(scritto); carica(scritto); }}
                    disabled={scritto.trim().length < 4}>
              Entra
            </Button>
          </CardContent>
        </Card>
      </Schermo>
    );
  }

  /* ----------------------------------------------------- chi sei, e il test */
  return (
    <Schermo>
      <div className="mb-4">
        <div className="text-xs uppercase tracking-[0.2em] text-orange-600">Verifica dell&apos;apprendimento</div>
        <h1 className="font-display text-2xl font-black tracking-tighter leading-tight">{dati.corso}</h1>
      </div>

      {!chiSono && (
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm font-semibold mb-2">Chi sei?</p>
            <div className="space-y-1.5">
              {dati.partecipanti.map((p) => (
                <button key={p.id} type="button" disabled={p.consegnato}
                        onClick={() => setChiSono(p.id)}
                        data-testid={`sono-${p.id}`}
                        className={`w-full text-left px-3 py-3 rounded border text-sm
                          ${p.consegnato
                            ? "border-slate-100 bg-slate-50 text-slate-400"
                            : "border-slate-200 hover:border-orange-400"}`}>
                  {p.nome}{p.consegnato && " · ha già consegnato"}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {chiSono && (
        <>
          <div className="sticky top-0 bg-slate-50 py-2 z-10">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
              <span className="font-semibold text-slate-700">{io?.nome}</span>
              <span>{date} di {quante}</span>
            </div>
            <div className="h-1.5 bg-slate-200 rounded overflow-hidden">
              <div className="h-full bg-orange-500 transition-all"
                   style={{ width: `${quante ? (date * 100) / quante : 0}%` }} />
            </div>
          </div>

          <div className="space-y-3 mt-2">
            {dati.domande.map((d, n) => (
              <Card key={d.id} className="border-slate-200">
                <CardContent className="p-4">
                  <p className="text-sm font-semibold mb-3">
                    <span className="text-orange-600">{n + 1}.</span> {d.testo}
                  </p>
                  <div className="space-y-1.5">
                    {d.opzioni.map((o, i) => {
                      const scelta = risposte[d.id] === i;
                      return (
                        <button key={i} type="button"
                                onClick={() => setRisposte((p) => ({ ...p, [d.id]: i }))}
                                data-testid={`risposta-${d.id}-${i}`}
                                className={`w-full text-left px-3 py-2.5 rounded border text-sm transition
                                  ${scelta ? "border-orange-500 bg-orange-50 font-semibold"
                                           : "border-slate-200 hover:border-slate-300"}`}>
                          {o}
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {errore && <p className="text-sm text-red-600 mt-3">{errore}</p>}

          <Button className="w-full h-14 mt-4 mb-10 bg-orange-500 hover:bg-orange-600 text-white text-base"
                  disabled={date < quante || invio} onClick={consegna} data-testid="consegna-test">
            <Send className="w-4 h-4 mr-2" />
            {invio ? "Consegno..." : date < quante
              ? `Manca${quante - date === 1 ? "" : "no"} ${quante - date} rispost${quante - date === 1 ? "a" : "e"}`
              : "Consegna il test"}
          </Button>
        </>
      )}
    </Schermo>
  );
}

function Schermo({ children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white">
        <div className="max-w-xl mx-auto px-4 py-3 flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-orange-400" />
          <span className="font-display font-bold">SGM Impianti · Formazione</span>
        </div>
      </header>
      <div className="max-w-xl mx-auto px-4 py-5">{children}</div>
    </div>
  );
}
